<template>
  <OrderTestDialog 
    :title="isShowCreatePatient ? 'Add New Patient' : 'Search Patient'" 
    :width="970" 
    style="min-height: 607px;"
    :is-show-dialog="isShowDialog"
    :show-footer="!isShowCreatePatient"
    @close="$emit('close')"
  >
    <div class="px-3" v-if="!isShowCreatePatient">
      <div class="flex items-center">
        <img :src="image_base_url + 'orderTest/family_discount_icon_light.svg'" alt="Family Discount" class="mr-1" />
        <p class="header-text">Add a patient to the family bundle</p>
      </div>
      <p class="mt-2 subtitle-text">All orders in the bundle will ship to <span class="font-medium">{{ shippingAddress }}</span></p>
      <div class="mt-8 px-5">
        <p>Search for patient</p>
        <SearchPatientInputAndResult 
          class="mt-1.5" 
          :disabled-patient-id="disabledPatientId"
          @clickSearchedPatient="clickSearchedPatient" 
          @createPatient="createPatient" 
        />
      </div>
    </div>
    <div v-else>
      <CreatePatientForm 
        :patient="newPatient"
        @back="isShowCreatePatient = false" 
        @createNewPatient="createNewPatient"
        @duplicatePatient="showDuplicatePatientDialog"
      />
      <DuplicatePatientDialog
        v-if="isShowDuplicatePatientDialog"
        :is-show-dialog="isShowDuplicatePatientDialog"
        :id="duplicatePatient.id"
        :name="[duplicatePatient.firstName, duplicatePatient.middleName, duplicatePatient.lastName].filter(Boolean).join(' ')"
        :gender="duplicatePatient.gender"
        :dob="duplicatePatient.birthdate"
        @close="isShowDuplicatePatientDialog = false"
      >
        <template #content>
          <div class="mt-6">
            <p class="font-medium text-base">Patient Already Exists</p>
            <div class="mt-2.5">
              <p>This patient already exists.</p>
              <p>Would you like to select this existing patient for the family bundle?</p>
            </div>
          </div>
        </template>
        <template #footer>
          <div class="flex justify-between">
            <LoginButton plain @click="isShowDuplicatePatientDialog = false">Back to Edit</LoginButton>
            <LoginButton type="primary" plain @click="selectDuplicatePatient">Select this patient</LoginButton>
          </div>
        </template>
      </DuplicatePatientDialog>
    </div>
    <template #footer>
      <LoginButton plain class="absolute right-12 bottom-9 w-32" @click="$emit('openChooseFamilyMemberDialog')">Back</LoginButton>
    </template>
  </OrderTestDialog>
</template>

<script>
import OrderTestDialog from "@/views/va-orderTest/components/OrderTestDialog.vue";
import SearchPatientInputAndResult from "@/views/va-orderTest/user-selection/patient-container/choose-patient/SearchPatientInputAndResult.vue";
import CreatePatientForm from "../../user-selection/patient-container/create-patient/CreatePatientForm.vue";
import DuplicatePatientDialog from "../../user-selection/patient-container/create-patient/DuplicatePatientDialog.vue";
import LoginButton from "@/views/login/components/LoginButton.vue";
import { autoFillPatientName, patient } from "../../user-selection/patient-container/create-patient/createPatientForm";
import { mapState } from "vuex";

export default {
  emits: ['close','openUpdateAddressDialog', 'openChooseFamilyMemberDialog', 'addFamilyMember'],
  components: {
    OrderTestDialog,
    SearchPatientInputAndResult,
    CreatePatientForm,
    DuplicatePatientDialog,
    LoginButton
  },
  inject: ['image_base_url'],
  props: {
    isShowDialog: {
      type: Boolean,
      default: false
    },
    shippingAddress: {
      type: String,
      default: ""
    },
    shippingAddressObj: {
      type: Object,
      default: () => ({
        streetAddress: "",
        city: "",
        state: "",
        zipcode: "",
        country: ""
      })
    }
  },
  data() {
    return {
      isShowCreatePatient: false,
      newPatient: { ...patient },
      duplicatePatient: null,
      isShowDuplicatePatientDialog: false,
    }
  },
  computed: {
    ...mapState("orderTest/order", ["patient"]),
    disabledPatientId() {
      return this.patient.id.toString();
    }
  },
  methods: {
    clickSearchedPatient(p) {
      let { street_address, city, state, zipcode, country } = p.patient_address[0];
      let paddress = [street_address, city, state, zipcode, country].filter(Boolean).join(", ");
      let patientObj = {
        name: [p.patient_first_name, p.patient_middle_name, p.patient_last_name].filter(Boolean).join(" "),
        gender: p.patient_gender,
        birthDate: p.patient_birthdate,
        id: p.patient_id,
        isSelected: true,
      }
      this.addFamilyMemberAndCheckAddress(patientObj, paddress);
    },
    createPatient(inputPatientName) {
      autoFillPatientName(this.newPatient, inputPatientName);
      let { streetAddress, city, state, zipcode, country } = this.shippingAddressObj;
      this.newPatient.streetAddress = streetAddress;
      this.newPatient.city = city;
      this.newPatient.state = state;
      this.newPatient.zipcode = zipcode;
      this.newPatient.country = country;
      this.isShowCreatePatient = true;
    },
    createNewPatient(patient) {
      let patientObj = {
        name: [patient.firstName, patient.middleName, patient.lastName].filter(Boolean).join(" "),
        gender: patient.gender,
        birthDate: patient.birthdate,
        id: patient.id,
        isSelected: true,
      }
      let { streetAddress, city, state, zipcode, country } = patient;
      let paddress = [streetAddress, city, state, zipcode, country].filter(Boolean).join(", ");
      this.addFamilyMemberAndCheckAddress(patientObj, paddress);
    },
    showDuplicatePatientDialog(duplicatePatient) {
      let patientAddress = {
        streetAddress: "",
        city: "",
        state: "",
        zipcode: "",
        country: "",
      };
      if (duplicatePatient.patient_address && duplicatePatient.patient_address[0]) {
        let duplicatePatientAddress = duplicatePatient.patient_address[0];
        patientAddress.streetAddress = duplicatePatientAddress.street_address;
        patientAddress.city = duplicatePatientAddress.city;
        patientAddress.state = duplicatePatientAddress.state;
        patientAddress.zipcode = duplicatePatientAddress.zipcode;
        patientAddress.country = duplicatePatientAddress.country;
      }
      this.duplicatePatient = {
        id: duplicatePatient.patientId,
        firstName: this.newPatient.firstName,
        middleName: this.newPatient.middleName,
        lastName: this.newPatient.lastName,
        gender: duplicatePatient.patient_gender,
        birthdate: duplicatePatient.patient_birthdate,
        ...patientAddress,
      };
      this.isShowDuplicatePatientDialog = true;
    },
    addFamilyMemberAndCheckAddress(patientObj, address) {
      if (address !== this.shippingAddress) {
        this.$emit('openUpdateAddressDialog', {...patientObj, currentAddress: address});
      } else {
        this.$store.dispatch("orderTest/familyDiscount/addFamilyMember", patientObj);
        this.$emit('openChooseFamilyMemberDialog');
      }
    },
    selectDuplicatePatient() {
      this.isShowDuplicatePatientDialog = false;
      let { id, firstName, middleName, lastName, gender, birthdate, streetAddress, city, state, zipcode, country } = this.duplicatePatient;
      let patientObj = {
        name: [firstName, middleName, lastName].filter(Boolean).join(" "),
        gender,
        birthDate: birthdate,
        id,
        isSelected: true, 
      }
      let paddress = [streetAddress, city, state, zipcode, country].filter(Boolean).join(", ");
      this.addFamilyMemberAndCheckAddress(patientObj, paddress);
    }
  }
}
</script>

<style scoped>
.header-text {
  color: #1B90AB;
  font-size: 20px;
  font-weight: 500;
}
.subtitle-text {
  color: #4A4A4A;
  font-size: 16px;
  font-weight: 400;
}
</style>
